import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  TextField,
  useTheme,
  alpha,
} from '@mui/material';
import { Trash2, AlertTriangle, X } from 'lucide-react';

/**
 * DeleteConfirmDialog - Specialized modal for permanent deletion tasks.
 * Highlights the target item, states irreversibility, supports typed confirmation for critical data.
 */
export default function DeleteConfirmDialog({
  open,
  onClose,
  onDelete,
  onConfirm,
  itemName = '',
  itemType = 'item', // 'product' | 'user' | 'category' | 'business' | etc.
  title,
  description,
  confirmText,
  cancelText = 'Cancel',
  loading = false,
  requireTypedConfirm = false, // If true, requires typing itemName to enable delete button
  warningText = 'This action cannot be undone and will permanently remove associated data.',
  maxWidth = 'xs',
}) {
  const theme = useTheme();
  const [typedInput, setTypedInput] = useState('');

  const displayTitle = title || `Delete ${itemName ? `"${itemName}"` : itemType}?`;
  const displayConfirmText = confirmText || `Delete ${itemType}`;

  const isConfirmDisabled =
    loading || (requireTypedConfirm && typedInput.trim().toLowerCase() !== (itemName || '').trim().toLowerCase());

  const handleClose = () => {
    setTypedInput('');
    if (!loading && onClose) onClose();
  };

  const handleConfirm = () => {
    if (isConfirmDisabled) return;
    const action = onDelete || onConfirm;
    if (action) action();
  };

  const titleId = 'delete-dialog-title';
  const descId = 'delete-dialog-description';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descId}
      slotProps={{
        paper: {
          sx: {
            p: 0,
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle
        id={titleId}
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          p: { xs: 2.5, sm: 3 },
          pb: 1.5,
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.error.lighter || alpha(theme.palette.error.main, 0.12),
              color: theme.palette.error.main,
              flexShrink: 0,
            }}
          >
            <Trash2 size={22} strokeWidth={2.2} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.01em', lineHeight: 1.25, color: 'text.primary' }}
            >
              {displayTitle}
            </Typography>
            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
              Permanent Deletion Warning
            </Typography>
          </Box>
        </Box>

        <IconButton
          aria-label="Close delete dialog"
          onClick={handleClose}
          disabled={loading}
          size="small"
          sx={{
            color: 'text.secondary',
            p: 0.5,
            borderRadius: 1.5,
            '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06), color: 'text.primary' },
          }}
        >
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, py: 1.5 }}>
        <Typography
          id={descId}
          variant="body2"
          sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 2 }}
        >
          {description || (
            <>
              Are you sure you want to permanently delete{' '}
              {itemName ? (
                <strong style={{ color: theme.palette.text.primary }}>{itemName}</strong>
              ) : (
                `this ${itemType}`
              )}
              ?
            </>
          )}
        </Typography>

        {warningText && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.error.main, 0.06),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              mb: requireTypedConfirm ? 2 : 0,
            }}
          >
            <AlertTriangle size={18} color={theme.palette.error.main} style={{ flexShrink: 0, marginTop: 2 }} />
            <Typography variant="caption" sx={{ color: 'error.dark', fontWeight: 600, lineHeight: 1.45 }}>
              {warningText}
            </Typography>
          </Box>
        )}

        {requireTypedConfirm && itemName && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              To verify, type <strong>{itemName}</strong> below:
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={typedInput}
              onChange={(e) => setTypedInput(e.target.value)}
              placeholder={itemName}
              disabled={loading}
              autoFocus
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.4) : alpha(theme.palette.grey[50], 0.8),
          gap: 1.25,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={{ minWidth: 88, fontWeight: 600 }}
        >
          {cancelText}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Trash2 size={16} />}
          sx={{
            minWidth: 120,
            fontWeight: 700,
            boxShadow: `0 2px 10px 0 ${alpha(theme.palette.error.main, 0.35)}`,
          }}
        >
          {loading ? 'Deleting...' : displayConfirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
